<template>
  <el-popover
    placement="bottom-end"
    trigger="click"
    :width="fit-content"
    :visible="popoverVisible"
  >
    <template #reference>
      <button class="btn add-filter-btn" @click="openPopover()">
        <i class="ri-add-fill medium-icon"></i>
      </button>
    </template>
    <div v-for="index in numItems" :key="index" class="filter-content">
      <div class="filter-row mb-2">
        <div
          class="filter-label-input mr-2"
          v-if="!newItems[index - 1].fieldOmitted"
        >
          <label>Field</label>
          <el-input
            class="filter"
            @update:model-value="value => newItems[index - 1].field = value"
            :class="{ 'is-invalid': newItems[index - 1].invalidFieldInput }"
            :model-value="newItems[index - 1].field"
            :disabled="!newItems[index - 1].fieldEnabled"
          />
          <span
            v-if="newItems[index - 1].invalidFieldInput"
            class="invalid-input-message"
            >*Invalid input</span
          >
        </div>
        <div class="filter-label-input mr-2">
          <label>Type</label>
          <el-select
            :model-value="newItems[index - 1].actualType"
            disabled
            placeholder="Select"
          >
          </el-select>
        </div>
        <div
          v-if="newItems[index - 1].type === 'selections'"
          class="filter-label-input"
        >
          <label>Value</label>
          <el-select
            :model-value="newItems[index - 1].value"
            @update:model-value="value => newItems[index - 1].value = value"
            placeholder="Select"
            :class="{ 'is-invalid': newItems[index - 1].invalidValueInput }"
          >
            <el-option
              v-for="item in newItems[index - 1].selections"
              :key="item"
              :label="item"
              :value="item"
            />
          </el-select>
          <span
            v-if="newItems[index - 1].invalidValueInput"
            class="invalid-input-message"
            >*Invalid input</span
          >
        </div>
        <div
          v-else-if="(newItems[index - 1].type === 'number' || newItems[index - 1].type === 'string')"
          class="filter-label-input"
        >
          <label>Value</label>
          <el-input
            class="filter"
            :class="{ 'is-invalid': newItems[index - 1].invalidValueInput }"
            :model-value="newItems[index - 1].value"
            @update:model-value="value => newItems[index - 1].value = value"
          />
          <span
            v-if="newItems[index - 1].invalidValueInput"
            class="invalid-input-message"
            >*Invalid input</span
          >
        </div>
        <div
          v-else-if="newItems[index - 1].type === 'boolean'"
          class="filter-label-input"
        >
          <label>Value</label>
          <el-select
            :model-value="newItems[index - 1].value"
            @update:model-value="value => newItems[index - 1].value = value"
            placeholder="Select"
          >
            <el-option
              v-for="item in [true, false]"
              :key="item"
              :label="item"
              :value="item"
            />
          </el-select>
        </div>
        <div
          v-else-if="newItems[index - 1].type === 'timestamp'"
          class="filter-label-input"
        >
          <label>Value</label>
          <VueDatePicker
            :class="{ 'is-invalid': newItems[index - 1].invalidValueInput }"
            v-model="newItems[index - 1].value"
          ></VueDatePicker>
          <span
            v-if="newItems[index - 1].invalidValueInput"
            class="invalid-input-message"
            >*Invalid input</span
          >
        </div>
        <div v-if="name === 'devices'" class="filter-label-input ml-2">
          <label>Device Type</label>
          <el-select
            placeholder="Select"
            :class="{ 'is-invalid': newItems[index - 1].invalidDeviceTypeInput }"
            :model-value="newItems[index - 1].deviceType"
            @update:model-value="value => newItems[index - 1].deviceType = value"
          >
            <el-option
              v-for="item in allDeviceTypes"
              :key="item"
              :label="item"
              :value="item"
            />
          </el-select>
          <span
            v-if="newItems[index - 1].invalidDeviceTypeInput"
            class="invalid-input-message"
            >*Invalid input</span
          >
        </div>
      </div>
    </div>
    <div class="filter-actions">
      <div class="action-buttons">
        <button @click="onCancelPopover(index - 1)" class="btn action-btn mr-2">
          Cancel
        </button>
        <button @click="handleAddToField" class="btn action-btn apply-btn">
          Update
        </button>
      </div>
    </div>
  </el-popover>
</template>

<script>
import { ref, onMounted } from 'vue'
import { updateDocument } from '../../../services/useDocument'
import { arrayUnion } from 'firebase/firestore'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import getAllDeviceTypes from '../../../composables/getAllDeviceTypes'
import updateDevice from '../../../composables/updateDevice'

export default {
  props: {
    documentId: {
      type: String
    },
    collection: {
      type: Object
    },
    schema: {
      type: Object
    },
    name: {
      type: String
    }
  },
  components: {
    VueDatePicker
  },
  setup (props, context) {
    const popoverVisible = ref(false)
    const numItems = ref(0)
    const { handlelUpdate } = updateDocument(props.collection.id, props.documentId)
    const { allDeviceTypes, loadDeviceTypes } = getAllDeviceTypes()

    const fieldType = ref(null)
    const newItems = ref([])

    onMounted(async () => {
      if (props.name === 'devices') {
        loadDeviceTypes()
      }
      fieldType.value = props.collection.fields[props.name] // array or object

      if (fieldType.value === 'object') {
        // Fixed key and value
        if (!props.schema.object) {
          numItems.value = 1
          newItems.value = [{
            field: '',
            value: '',
            type: props.schema.value,
            invalidValueInput: false,
            invalidFieldInput: false,
            fieldEnabled: true,
            actualType: props.schema.value === 'selections' ? 'string' : props.schema.value,
            selections: props.schema.selections
          }]
        }
        if (props.name === 'devices') {
          newItems.value[0].deviceType = ''
          newItems.value[0].invalidDeviceTypeInput = false
        }
      }

      if (fieldType.value === 'array') {
        const itemType = props.schema.itemType

        if (itemType === 'object') {
          numItems.value = Object.keys(props.schema.object).length
          for (const [key, value] of Object.entries(props.schema.object)) {
            newItems.value.push({
              field: key,
              value: '',
              type: value,
              invalidValueInput: false,
              actualType: value === 'selections' ? 'string' : value,
              selections: props.schema.selections ? props.schema.selections[key] : []
            })
          }
        } else {
          // Primitive type in array
          numItems.value = 1
          newItems.value = [{
            value: '',
            type: itemType,
            invalidValueInput: false,
            actualType: itemType === 'selections' ? 'string' : itemType,
            fieldOmitted: true
          }]
        }
      }
    })

    const togglePopovervisibility = () => {
      popoverVisible.value = !popoverVisible.value
    }

    const onCancelPopover = () => {
      popoverVisible.value = false
      for (const index in newItems.value) {
        newItems.value[index].invalidValueInput = false
        newItems.value[index].value = ''

        if (newItems.value[index].fieldEnabled) {
          newItems.value[index].field = ''
          newItems.value[index].invalidFieldInput = false
        }
      }
    }

    const dateChanged = (date, index) => {
      newItems.value[index].value = date
      newItems.value[index].invalidValueInput = false
    }
    const openPopover = () => {
      togglePopovervisibility()
    }

    const validateInputValue = (item) => {
      let newValue
      item.invalidValueInput = false

      if (props.name === 'devices') {
        item.invalidDeviceTypeInput = false
        if (item.deviceType === '') {
          item.invalidDeviceTypeInput = true
          return false
        }
      }
      switch (item.actualType) {
        case 'number':
          newValue = parseFloat(item.value)
          if (isNaN(newValue)) {
            item.invalidValueInput = true
            return false
          }
          item.value = newValue
          return true
        case 'boolean':
          return true
        case 'string':
          if (item.value === '') {
            item.invalidValueInput = true
            return false
          }
          return true
        case 'timestamp':
          newValue = new Date(item.value)
          if (isNaN(newValue)) {
            item.invalidValueInput = true
            return false
          }
          item.value = newValue
          return true
        default:
      }
    }

    const validateInputField = (item) => {
      item.invalidFieldInput = false
      if (item.field === '') {
        item.invalidFieldInput = true
        return false
      }
      return true
    }

    const addPrimitiveTypeToArray = async () => {
      await handlelUpdate({
        [`${props.name}`]: arrayUnion(newItems.value[0].value)
      })
    }

    const addNewEntryToMap = async () => {
      if (props.name === 'devices') {
        const { updateDeviceBasedOnId } = updateDevice()
        await updateDeviceBasedOnId(newItems.value[0].field, newItems.value[0].deviceType, props.documentId)
      }
      await handlelUpdate({
        [`${props.name}.${newItems.value[0].field}`]: newItems.value[0].value
      })
    }

    const addObjectToArray = async () => {
      const newObject = {}
      for (const index in newItems.value) {
        newObject[newItems.value[index].field] = newItems.value[index].value
      }
      await handlelUpdate({
        [`${props.name}`]: arrayUnion(newObject)
      })
    }

    const handleAddToField = async () => {
      if (fieldType.value === 'array') {
        const itemType = props.schema.itemType
        if (itemType === 'object') {
          // Objects in array field
          for (const index in newItems.value) {
            if (!validateInputValue(newItems.value[index])) {
              console.log(newItems.value[index])
              return
            }
          }
          await addObjectToArray()
          onCancelPopover()
        } else {
          // Primitives in array field
          if (!validateInputValue(newItems.value[0])) {
            return
          }
          await addPrimitiveTypeToArray()
          onCancelPopover()
        }
      } else {
        // Object field
        // Fixed key and value
        if (!validateInputField(newItems.value[0]) || !validateInputValue(newItems.value[0])) {
          return
        }
        await addNewEntryToMap()
        onCancelPopover()
      }
    }

    return {
      popoverVisible,
      togglePopovervisibility,
      onCancelPopover,
      openPopover,
      handleAddToField,
      numItems,
      newItems,
      dateChanged,
      allDeviceTypes
    }
  }
}
</script>

<style scoped>
.filter-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  font-size: 13px !important;
}

.medium-icon {
  font-size: 16px;
  color: #a09e9e;
}

.document-title {
  color: #3f414d;
}

.filter-row span {
  white-space: nowrap;
  margin-right: 8px;
}

.filter {
  flex: 1;
  margin-right: 8px;
}

.filter-select {
  min-width: 190px;
}

.filter-content {
  margin: 10px 10px 20px 10px;
  display: flex;
  flex-direction: column;
}

.filter-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid var(--border-color);
  padding-top: 15px;
  margin-left: -15px;
  margin-right: -15px;
  padding-left: 15px;
  padding-right: 15px;
}

.filter-label-input {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 10px;
  position: relative;
}

.filter-label-input label {
  margin-bottom: 10px;
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 10px;
}

.action-buttons {
  display: flex;
}

.filter-label-input label {
  margin-bottom: 0.5rem;
}

.filter {
  width: 100%;
}

.action-btn {
  background-color: #e1e5e8;
  color: black;
  font-size: 13px !important;
}

.apply-btn {
  background-color: green;
  color: white;
}

.apply-btn:hover {
  color: white;
  background-color: darkgreen;
}
</style>

<style>
.dp__action_button {
  text-wrap: nowrap !important;
  font-size: 12px !important;
  padding: 10px !important;
  margin-left: 5px !important;
}

.dp__action_button.dp__action_select {
  color: white !important;
}

.filter-label-input .is-invalid + .invalid-input-message {
  visibility: visible;
}

.filter-label-input .is-invalid ~ .invalid-input-message {
  visibility: visible;
}

.invalid-input-message {
  color: #ff4d4f;
  font-size: 11px;
  position: absolute;
  bottom: -20px;
  left: 0;
  visibility: hidden;
}
</style>
